import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

// Pages
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Home from './pages/Home';
import PrintStore from './pages/PrintStore';
import Wallet from './pages/Wallet';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import Checkout from './pages/Checkout';
import PrintServicePage from './pages/PrintServicePage';
import PassportPhotos from './pages/PassportPhotos';
import OnDemandPrint from './pages/OnDemandPrint';
import PvcCard from './pages/PvcCard';
import MartCategory from './pages/MartCategory';
import ProductDetail from './pages/ProductDetail';
import Profile from './pages/Profile';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import ManageOrders from './pages/admin/ManageOrders';
import ManageProducts from './pages/admin/ManageProducts';
import ManageUsers from './pages/admin/ManageUsers';
import AddProduct from './pages/admin/AddProduct';
import ManageRates from './pages/admin/ManageRates';

// Components
import BottomNav from './components/BottomNav';
import AIBubble from './components/AIBubble';
import AppInstallBanner from './components/AppInstallBanner';
import CookieConsentBanner from './components/CookieConsentBanner';
import ProtectedRoute from './components/ProtectedRoute';
import PrintOrderForm from './components/PrintOrderForm';

function App() {
  const { user, loading } = useAuth();
  const [showQuoteModal, setShowQuoteModal] = React.useState(false);

  React.useEffect(() => {
    const handleOpenQuote = () => setShowQuoteModal(true);
    window.addEventListener('open-print-quote', handleOpenQuote);
    return () => {
      window.removeEventListener('open-print-quote', handleOpenQuote);
    };
  }, []);

  React.useEffect(() => {
    const handleWheel = (e) => {
      if (document.activeElement && document.activeElement.type === 'number') {
        document.activeElement.blur();
      }
    };
    document.addEventListener('wheel', handleWheel, { passive: true });
    return () => {
      document.removeEventListener('wheel', handleWheel);
    };
  }, []);

  if (loading) {
    return (
      <div className="loading-page">
        <div className="spinner"></div>
        <p style={{ color: 'var(--text-secondary)' }}>Loading...</p>
      </div>
    );
  }

  return (
    <div className="App">
      <Routes>
        {/* Public */}
        <Route path="/login" element={user ? <Navigate to="/" /> : <Login />} />
<Route path="/forgot-password" element={user ? <Navigate to="/" /> : <ForgotPassword />} />
<Route path="/reset-password" element={user ? <Navigate to="/" /> : <ResetPassword />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/instant-quote" element={<PrintOrderForm />} />
        <Route path="/quote" element={<PrintOrderForm />} />

        {/* Protected User Routes */}
        <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
        <Route path="/print-store" element={<ProtectedRoute><PrintStore /></ProtectedRoute>} />
        <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
        <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
        <Route path="/orders/:id" element={<ProtectedRoute><OrderDetail /></ProtectedRoute>} />
        <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
        <Route path="/print/:id" element={<ProtectedRoute><PrintServicePage /></ProtectedRoute>} />
        <Route path="/passport-photos" element={<ProtectedRoute><PassportPhotos /></ProtectedRoute>} />
        <Route path="/on-demand-print" element={<ProtectedRoute><OnDemandPrint /></ProtectedRoute>} />
        <Route path="/pvc-card" element={<ProtectedRoute><PvcCard /></ProtectedRoute>} />
        <Route path="/mart/:categoryId" element={<ProtectedRoute><MartCategory /></ProtectedRoute>} />
        <Route path="/product/:id" element={<ProtectedRoute><ProductDetail /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />

        {/* Admin Routes */}
        <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/orders" element={<ProtectedRoute adminOnly><ManageOrders /></ProtectedRoute>} />
        <Route path="/admin/products" element={<ProtectedRoute adminOnly><ManageProducts /></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute adminOnly><ManageUsers /></ProtectedRoute>} />
        <Route path="/admin/add-product" element={<ProtectedRoute adminOnly><AddProduct /></ProtectedRoute>} />
        <Route path="/admin/rates" element={<ProtectedRoute adminOnly><ManageRates /></ProtectedRoute>} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>

      {/* Bottom Nav & AI Bubble - only show when logged in */}
      {user && (
        <>
          <BottomNav />
          <AIBubble />
        </>
      )}

      {/* Promote to install live Android App (dismissable, with QR & Google Play link) */}
      <AppInstallBanner />

      {/* Floating Instant Print Quote Button */}
      <button
        type="button"
        className="pof-floating-trigger"
        onClick={() => setShowQuoteModal(true)}
        title="Get Instant Print Quote"
        aria-label="Get Instant Print Quote"
      >
        <span>✨</span>
        <span>Instant Quote</span>
      </button>

      {/* Modern Luxury Quotation Modal */}
      {showQuoteModal && (
        <PrintOrderForm onClose={() => setShowQuoteModal(false)} />
      )}

      {/* Floating Cookie Consent Banner */}
      <CookieConsentBanner />
    </div>
  );
}

export default App;
